import UmlUndoCache from './UmlUndoCache';
import { collectShapeIdsDeep } from '../UmlCanvasEngineHelper';

/**
 * Undo engine for the UML canvas — pure pub/sub singleton, no React
 * knowledge here (same pattern as UmlCanvasEngine/UmlConnectorEngine).
 *
 * Deliberately dumb: the engine only stores { type, data } snapshots and
 * hands them back on undo(). It has NO knowledge of what "data" means for a
 * given type — that interpretation lives entirely in UmlUndoRestorer. This
 * keeps the engine reusable for future action types without ever needing to
 * change.
 */
class UmlUndoEngine {
    constructor() {
        this.cache = new UmlUndoCache();
    }

    /**
     * Builds the { type, data } snapshot for a shape delete.
     *
     * WHY THIS PULLS IN CONNECTORS TOO:
     * A shape's nested children (UmlCompositeShape.children) come along for
     * free in a deep copy — they already live INSIDE the shape object.
     * Connectors do NOT. They live in a completely separate flat list
     * (UmlConnectorEngine.connectors) and only ever REFERENCE a shape by id.
     * A deep copy of `shape` alone would silently drop every connector — on
     * restore you'd get a package back with no lines attached, even though
     * it had some before delete.
     *
     * THIS IS ALSO WHY TODAY'S DELETE HAD A REAL BUG (independent of undo):
     * UmlCanvasEngine.removeShape used to clean up connectors using ONLY the
     * top-level shape's own id — never any nested child id. A connector on
     * a class NESTED inside a deleted package was never removed; it
     * survived pointing at a shape id that no longer exists. Fixed
     * alongside this snapshot, because a correct snapshot and a correct
     * delete both need the SAME thing: every shape id in the deleted
     * subtree, gathered recursively (see collectShapeIdsDeep).
     *
     * @param {object} shape - the shape being deleted (top-level or nested)
     * @param {Array} connectors - UmlConnectorEngine.connectors, the live list
     * @returns {{shape: object, connectors: Array}} deep-cloned snapshot data
     */
    createSnapshot(shape, connectors) {
        const shapeIds = collectShapeIdsDeep(shape);
        const relatedConnectors = connectors.filter(function filterRelatedConnector(connector) {
            return shapeIds.includes(connector.fromShapeId) || shapeIds.includes(connector.toShapeId);
        });
        // Deep clone: the snapshot must survive the live shape/connectors
        // being mutated or removed right after this call.
        return JSON.parse(JSON.stringify({ shape, connectors: relatedConnectors }));
    }

    /**
     * Pushes an action onto the undo stack.
     * @param {string} type - e.g. 'delete'
     * @param {object} data - snapshot data for this type, see createSnapshot
     */
    addAction(type, data) {
        this.cache.push({ type, data });
    }

    /**
     * Pops the most recent action off the stack.
     * @returns {{type: string, data: object}|null} null if nothing to undo
     */
    undo() {
        return this.cache.pop();
    }
}

const umlUndoEngine = new UmlUndoEngine();
export default umlUndoEngine;
